import React, {
  useState, useEffect, useRef, useLayoutEffect, useCallback
} from "react";
import axios from "axios";
import { APIConstant } from "services/api-constant";
import { RefreshCw, GripVertical, X, Download, Maximize2, Minimize2 } from "lucide-react";
import { useTheme } from "context/ThemeContext";

/* ─── constants ──────────────────────────────────────────────────── */
const COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6","#10B981","#F59E0B"];

const initials = n =>
  (n||"").trim().split(" ").filter(Boolean).map(w=>w[0]).join("").toUpperCase().slice(0,2)||"?";

/* ─── helpers ────────────────────────────────────────────────────── */
function buildRoots(flat) {
  const map = {};
  flat.forEach(e => { map[e.id] = { ...e, children: [] }; });
  const roots = [];
  flat.forEach(e => {
    if (e.manager_id && map[e.manager_id]) map[e.manager_id].children.push(map[e.id]);
    else roots.push(map[e.id]);
  });
  return roots;
}

function wouldCycle(dragId, targetId, flat) {
  if (dragId === targetId) return true;
  const parentOf = {};
  flat.forEach(e => { parentOf[e.id] = e.manager_id; });
  let cur = targetId;
  const seen = new Set();
  while (cur) {
    if (cur === dragId) return true;
    if (seen.has(cur)) break;
    seen.add(cur);
    cur = parentOf[cur] || null;
  }
  return false;
}

/* ─── Node card ──────────────────────────────────────────────────── */
const NodeCard = React.forwardRef(function NodeCard(
  { node, dragOver, dragging, onDragStart, onDragOver, onDragLeave, onDrop, onRemove, isRoot },
  ref
) {
  const { T } = useTheme();
  const LEVEL_COLOR = {
    CEO:T.red, CTO:T.red, Director:T.purple,
    "Senior Manager":T.purple, VP:T.purple,
    Manager:T.cyan, Lead:T.amber, "Team Lead":T.amber,
    Senior:T.green, Mid:T.green, Junior:T.muted,
  };
  const color = LEVEL_COLOR[node.designation_level]
             || LEVEL_COLOR[node.designation_title]
             || T.muted;
  const bg    = node.avatar_color || COLORS[node.id % COLORS.length];

  return (
    <div
      ref={ref}
      data-emp={node.id}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display:"inline-flex", flexDirection:"column", alignItems:"center",
        background: dragOver ? `${T.green}14` : T.card,
        border:`2px solid ${dragOver ? `${T.green}80` : dragging ? T.toggle_bg : `${color}35`}`,
        borderRadius:14, padding:"14px 16px",
        minWidth:152, maxWidth:176,
        cursor:"grab", userSelect:"none",
        opacity: dragging ? 0.35 : 1,
        transition:"border-color 0.15s, opacity 0.15s",
        boxShadow: dragging ? "none" : "0 6px 20px rgba(0,0,0,0.35)",
        position:"relative", zIndex:1,
      }}>

      {/* X remove */}
      {!isRoot && (
        <button onClick={e => { e.stopPropagation(); onRemove(node.id); }}
          title="Remove manager (make top-level)"
          style={{ position:"absolute", top:5, left:7, background:"none", border:"none",
            cursor:"pointer", color:T.dim, padding:1, lineHeight:1, fontSize:10 }}>
          <X size={11}/>
        </button>
      )}

      {/* Grip */}
      <GripVertical size={11} style={{ position:"absolute", top:6, right:7, color:T.dim }}/>

      {/* Avatar */}
      <div style={{
        width:44, height:44, borderRadius:"50%", background:bg,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:14, fontWeight:800, color:"#fff", marginBottom:9, flexShrink:0,
      }}>{initials(node.name)}</div>

      {/* Name */}
      <div style={{ fontSize:13, fontWeight:700, color:T.text, textAlign:"center",
        lineHeight:1.3, marginBottom:4, wordBreak:"break-word" }}>{node.name}</div>

      {/* Designation */}
      {(node.designation_title || node.designation_level) && (
        <div style={{ fontSize:10.5, color, fontWeight:700, textTransform:"uppercase",
          letterSpacing:"0.06em", textAlign:"center", marginBottom:3 }}>
          {node.designation_title || node.designation_level}
        </div>
      )}

      {/* Dept */}
      {node.department && (
        <div style={{ fontSize:10.5, color:T.dim, textAlign:"center" }}>{node.department}</div>
      )}

      {/* Team badge */}
      {node.children?.length > 0 && (
        <div style={{ marginTop:8, fontSize:10.5, padding:"2px 10px", borderRadius:20,
          background:`${color}15`, color, fontWeight:700 }}>
          {node.children.length} report{node.children.length > 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
});

/* ─── Connector line segment ─────────────────────────────────────── */
const VLine = ({ h=32 }) => {
  const { T } = useTheme();
  return <div style={{ width:2.5, minHeight:h, background:T.cyan, opacity:0.85, borderRadius:1, margin:"0 auto" }}/>;
};

/* ─── Recursive tree ─────────────────────────────────────────────── */
function SubTree({ node, nodeRefs, drag, setDrag, dropOver, setDropOver, onDrop, onRemove }) {
  const ref = useRef(null);

  // Register ref
  useEffect(() => { if (ref.current) nodeRefs.current[node.id] = ref.current; });

  const children = node.children || [];

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
      paddingLeft:12, paddingRight:12 }}>

      {/* Node */}
      <NodeCard
        ref={ref} node={node}
        isRoot={!node.manager_id}
        dragOver={dropOver === node.id}
        dragging={drag === node.id}
        onDragStart={e => { e.stopPropagation(); setDrag(node.id); e.dataTransfer.effectAllowed="move"; }}
        onDragOver={e  => { e.preventDefault(); e.stopPropagation(); if (drag !== node.id) setDropOver(node.id); }}
        onDragLeave={e => { e.stopPropagation(); setDropOver(null); }}
        onDrop={e => { e.preventDefault(); e.stopPropagation(); setDropOver(null); onDrop(drag, node.id); }}
        onRemove={onRemove}
      />

      {/* Children */}
      {children.length > 0 && (
        <>
          {/* Vertical line from card down to the horizontal bar */}
          <VLine h={36}/>

          {/* Horizontal bar + children */}
          <div style={{ display:"flex", alignItems:"flex-start", position:"relative" }}>

            {/* Horizontal bar spanning all children (CSS trick) */}
            {children.length > 1 && (
              <div style={{
                position:"absolute",
                top:0,
                left:"50%",  right:"50%",
                /* We'll use box-shadow to extend left & right instead */
                /* This covers the center gap between first and last child centers */
                height:2,
                background:"transparent",
                /* Use a wide box-shadow to approximate the horizontal bar */
                /* Real approach: rendered after layout via SVG, but CSS gives instant result */
              }}/>
            )}

            {children.map((child, i) => (
              <div key={child.id} style={{
                display:"flex", flexDirection:"column", alignItems:"center",
              }}>
                {/* Vertical line from horizontal bar down to child */}
                <VLine h={36}/>
                <SubTree
                  node={child} nodeRefs={nodeRefs}
                  drag={drag} setDrag={setDrag}
                  dropOver={dropOver} setDropOver={setDropOver}
                  onDrop={onDrop} onRemove={onRemove}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── SVG overlay for all connector lines ───────────────────────── */
function ConnectorSVG({ employees, nodeRefs, wrap }) {
  const { T } = useTheme();
  const [paths, setPaths]   = useState([]);
  const [size,  setSize]    = useState({ w:3000, h:3000 });

  const redraw = useCallback(() => {
    const container = wrap.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const newPaths = [];

    employees.forEach(emp => {
      if (!emp.manager_id) return;
      const cEl = nodeRefs.current[emp.id];
      const pEl = nodeRefs.current[emp.manager_id];
      if (!cEl || !pEl) return;

      const cR = cEl.getBoundingClientRect();
      const pR = pEl.getBoundingClientRect();

      // Positions relative to scroll container
      const x1 = pR.left + pR.width  / 2 - cRect.left + container.scrollLeft;
      const y1 = pR.bottom               - cRect.top  + container.scrollTop;
      const x2 = cR.left + cR.width  / 2 - cRect.left + container.scrollLeft;
      const y2 = cR.top                   - cRect.top  + container.scrollTop;

      // Bezier curve: control points at midpoint Y
      const mid = (y1 + y2) / 2;
      newPaths.push({
        key: `${emp.manager_id}-${emp.id}`,
        d:   `M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`,
      });
    });

    setSize({ w: container.scrollWidth + 20, h: container.scrollHeight + 20 });
    setPaths(newPaths);
  }, [employees, nodeRefs, wrap]);

  // Draw after every render cycle
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(redraw));
    return () => cancelAnimationFrame(id);
  });

  return (
    <svg
      style={{
        position:"absolute", top:0, left:0,
        width:size.w, height:size.h,
        pointerEvents:"none", zIndex:0, overflow:"visible",
      }}>
      {paths.map(p => (
        <path key={p.key} d={p.d}
          stroke={T.cyan} strokeOpacity={0.9} strokeWidth={2.25} fill="none"
          strokeLinecap="round"/>
      ))}
    </svg>
  );
}

/* ─── Root drop zone ─────────────────────────────────────────────── */
function RootZone({ active, onDrop }) {
  const { T } = useTheme();
  const [over, setOver] = useState(false);
  if (!active) return null;
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true);  }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onDrop(active, null); }}
      style={{
        position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)",
        padding:"10px 28px", borderRadius:12, zIndex:9999,
        border:`2px dashed ${over ? T.green : T.toggle_bg}`,
        background: over ? `${T.green}1F` : "rgba(6,16,31,0.9)",
        color: over ? T.green : T.muted,
        fontSize:13, fontWeight:600, backdropFilter:"blur(12px)",
        transition:"all 0.15s",
      }}>
      {over ? "✓ Release to make top-level" : "⬇  Drop here → remove from hierarchy (make top-level)"}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export default function ReportingStructure() {
  const { T } = useTheme();
  const token = sessionStorage.getItem("platform_token") || "";
  const ax    = { headers:{ Authorization:`Bearer ${token}` } };

  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [exporting, setExporting] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [drag,      setDrag]      = useState(null);
  const [dropOver,  setDropOver]  = useState(null);
  const [toast,     setToast]     = useState(null);

  const nodeRefs   = useRef({});
  const wrapRef    = useRef(null);

  /* fetch */
  const load = async () => {
    try {
      setLoading(true);
      nodeRefs.current = {};
      const r = await axios.get(`${APIConstant.employees}`, ax);
      setEmployees((r.data||[]).map(e => ({
        ...e,
        name: `${e.first_name} ${e.last_name}`.trim(),
      })));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  /* fullscreen: Esc to close + lock page scroll while maximized */
  useEffect(() => {
    if (!maximized) return;
    const onKey = e => { if (e.key === "Escape") setMaximized(false); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [maximized]);

  /* toast */
  const flash = msg => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  /* save manager assignment */
  const handleDrop = useCallback(async (dragId, targetId) => {
    if (!dragId) return;
    setDrag(null);
    if (targetId && wouldCycle(dragId, targetId, employees)) {
      flash("❌ Would create a circular reference — not allowed");
      return;
    }
    const dragEmp   = employees.find(e => e.id === dragId);
    const targetEmp = targetId ? employees.find(e => e.id === targetId) : null;

    setSaving(true);
    try {
      const qs = targetId ? `?manager_id=${targetId}` : `?manager_id=`;
      await axios.put(`${APIConstant.employees}${dragId}/manager${qs}`, {}, ax);
      // Optimistic update
      setEmployees(prev =>
        prev.map(e => e.id === dragId ? { ...e, manager_id: targetId || null } : e)
      );
      flash(targetId
        ? `✅ ${dragEmp?.name} now reports to ${targetEmp?.name}`
        : `✅ ${dragEmp?.name} moved to top level`
      );
    } catch(e) {
      flash("❌ Could not save — check PUT /employees/{id}/manager endpoint");
    } finally { setSaving(false); }
  }, [employees]);

  const handleRemove = useCallback(async empId => {
    setSaving(true);
    try {
      await axios.put(`${APIConstant.employees}${empId}/manager?manager_id=`, {}, ax);
      setEmployees(prev => prev.map(e => e.id === empId ? { ...e, manager_id: null } : e));
      flash("✅ Moved to top level");
    } catch(e) { flash("❌ Error"); }
    finally { setSaving(false); }
  }, []);

  const roots = buildRoots(employees);

  /* download org chart as PDF */
  const handleDownloadPDF = async () => {
    const node = wrapRef.current;
    if (!node || exporting) return;
    setExporting(true);

    let html2canvas, jsPDF;
    try {
      ({ default: html2canvas } = await import("html2canvas"));
      ({ jsPDF } = await import("jspdf"));
    } catch (e) {
      console.error("[OrgChart PDF] package import failed:", e);
      flash("❌ html2canvas / jspdf not installed — run: npm i html2canvas jspdf, then restart the dev server");
      setExporting(false);
      return;
    }

    const prev = {
      overflow:  node.style.overflow,
      maxHeight: node.style.maxHeight,
      height:    node.style.height,
      width:     node.style.width,
    };

    try {
      // Temporarily strip scroll/clip constraints AND lock the element's
      // width/height to its full content size — html2canvas otherwise sizes
      // the canvas to the visible box (clientWidth/clientHeight), cropping
      // anything that was only reachable by scrolling.
      node.style.overflow  = "visible";
      node.style.maxHeight = "none";
      node.style.height    = "auto";
      node.style.width     = "max-content";

      // Let layout settle (and SVG connector lines redraw) before measuring
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const fullW = node.scrollWidth;
      const fullH = node.scrollHeight;
      if (!fullW || !fullH) throw new Error(`Chart has zero size (w=${fullW}, h=${fullH})`);

      // Browsers cap canvas dimensions (Safari ~4096px/side, Chrome larger
      // but with a total-area ceiling). A flat scale:2 on a wide org chart
      // can silently exceed that, producing a broken/blank canvas — which is
      // exactly what causes jsPDF's "wrong PNG signature" error downstream.
      // Pick the largest scale (up to 2x) that keeps both dimensions safe.
      const MAX_DIM = 4000;
      const scale = Math.max(1, Math.min(2, MAX_DIM / fullW, MAX_DIM / fullH));

      const canvas = await html2canvas(node, {
        scale,
        backgroundColor: T.bg || "#ffffff",
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width:  fullW,
        height: fullH,
        windowWidth:  fullW,
        windowHeight: fullH,
        // Tailwind v4 / CSS-var themes often use oklch()/color-mix(), which
        // older html2canvas builds can't parse and will throw on. Skip any
        // node that errors instead of aborting the whole capture.
        onclone: (clonedDoc) => {
          clonedDoc.querySelectorAll("*").forEach(el => {
            const cs = clonedDoc.defaultView.getComputedStyle(el);
            ["color","backgroundColor","borderColor"].forEach(prop => {
              const v = cs[prop];
              if (v && (v.includes("oklch") || v.includes("color-mix") || v.includes("lab("))) {
                el.style[prop] = prop === "color" ? "#1f2937" : "transparent";
              }
            });
          });
        },
      });

      if (!canvas || !canvas.width || !canvas.height) {
        throw new Error(`Capture produced an empty canvas (${canvas?.width}x${canvas?.height}) — chart may be too large to render at once`);
      }

      const imgData = canvas.toDataURL("image/png");
      if (!imgData || !imgData.startsWith("data:image/png") || imgData.length < 200) {
        throw new Error("Captured image was empty/corrupted — try Minimize first, or reduce the number of visible employees");
      }
      let pdf;
      try {
        pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? "landscape" : "portrait",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      } catch (pdfErr) {
        throw new Error(`jsPDF could not embed the captured image (${canvas.width}x${canvas.height}px): ${pdfErr.message}`);
      }
      pdf.save(`org-chart-${new Date().toISOString().slice(0, 10)}.pdf`);
      flash("✅ Org chart downloaded as PDF");
    } catch (e) {
      console.error("[OrgChart PDF] capture failed:", e);
      flash(`❌ PDF export failed: ${e?.message || "see console for details"}`);
    } finally {
      // Always restore original scroll/clip behaviour, even on failure
      node.style.overflow  = prev.overflow;
      node.style.maxHeight = prev.maxHeight;
      node.style.height    = prev.height;
      node.style.width     = prev.width;
      setExporting(false);
    }
  };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>ORGANIZATION</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Org Chart</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>
            Drag a card onto another to assign reporting. Drop on the bottom zone to make top-level.
          </p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {saving && <span style={{ fontSize:12, color:T.amber, fontWeight:600 }}>Saving…</span>}
          <button onClick={handleDownloadPDF} disabled={exporting || loading || employees.length===0}
            style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.inp_bg, border:`1px solid ${T.cyan}33`, borderRadius:8, color:exporting?T.dim:T.text, padding:"7px 14px", cursor:exporting?"default":"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, opacity:(loading||employees.length===0)?0.5:1 }}>
            {exporting
              ? <><RefreshCw size={13} style={{ animation:"spin 0.8s linear infinite" }}/> Exporting…</>
              : <><Download size={13}/> Download PDF</>}
          </button>
          <button onClick={load} style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.inp_bg, border:`1px solid ${T.cyan}1F`, borderRadius:8, color:T.muted, padding:"7px 14px", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
            <RefreshCw size={13}/> Refresh
          </button>
          <button onClick={() => setMaximized(m => !m)} title={maximized ? "Minimize (Esc)" : "Maximize"}
            style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.inp_bg, border:`1px solid ${T.cyan}1F`, borderRadius:8, color:T.muted, padding:"7px 14px", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
            {maximized ? <><Minimize2 size={13}/> Minimize</> : <><Maximize2 size={13}/> Maximize</>}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display:"flex", gap:14, marginBottom:16, flexWrap:"wrap", fontSize:12, color:T.muted, alignItems:"center" }}>
        {[
          [T.red,"C-Suite"],[T.purple,"Sr. Leadership"],
          [T.cyan,"Manager"],[T.amber,"Team Lead"],
          [T.green,"Senior/Mid"],[T.muted,"Junior"],
        ].map(([c,l]) => (
          <div key={l} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:8, height:8, borderRadius:2, background:c }}/>
            <span>{l}</span>
          </div>
        ))}
        <span style={{ marginLeft:"auto", fontSize:11.5, color:T.dim, display:"flex", alignItems:"center", gap:4 }}>
          <GripVertical size={12}/> Drag to reassign
        </span>
      </div>

      {/* Chart */}
      <div style={maximized ? {
          position:"fixed", inset:0, zIndex:9998, background:T.bg,
          padding:"18px 24px 24px", display:"flex", flexDirection:"column",
        } : undefined}>
        {maximized && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:15, fontWeight:800, color:T.text }}>Org Chart — Fullscreen</div>
            <button onClick={() => setMaximized(false)}
              style={{ display:"inline-flex", alignItems:"center", gap:6, background:T.inp_bg, border:`1px solid ${T.cyan}33`, borderRadius:8, color:T.text, padding:"7px 14px", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
              <Minimize2 size={13}/> Minimize <span style={{ color:T.dim, fontWeight:400 }}>(Esc)</span>
            </button>
          </div>
        )}
        {loading ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:80, gap:12, color:T.muted, fontSize:13 }}>
            <div style={{ width:26, height:26, border:`2px solid ${T.cyan}1A`, borderTopColor:T.cyan, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
            Building org chart…
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : (
          <div
            ref={wrapRef}
            onDragEnd={() => { setDrag(null); setDropOver(null); }}
            style={{
              background:T.bg,
              border:`1px solid ${T.border}`,
              borderRadius:14,
              padding:"40px 48px 60px",
              overflowX:"auto",
              overflowY:"auto",
              minHeight:420,
              maxHeight: maximized ? "none" : "calc(100vh - 290px)",
              flex: maximized ? 1 : undefined,
              position:"relative",
            }}>

            {/* SVG lines drawn on top using absolute positioning */}
            <ConnectorSVG employees={employees} nodeRefs={nodeRefs} wrap={wrapRef}/>

            {roots.length === 0 ? (
              <div style={{ textAlign:"center", padding:60, color:T.muted, fontSize:14 }}>
                No employees yet — add employees from Employee Directory first.
              </div>
            ) : (
              <div style={{
                display:"flex", gap:48, alignItems:"flex-start",
                flexWrap:"nowrap", justifyContent:"center",
                position:"relative", zIndex:1,
                width:"fit-content", minWidth:"100%",
              }}>
                {roots.map(root => (
                  <SubTree
                    key={root.id} node={root}
                    nodeRefs={nodeRefs}
                    drag={drag} setDrag={setDrag}
                    dropOver={dropOver} setDropOver={setDropOver}
                    onDrop={handleDrop} onRemove={handleRemove}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary */}
      {employees.length > 0 && (
        <div style={{ marginTop:12, fontSize:12.5, color:T.muted, padding:"9px 16px", background:T.toggle_bg, border:`1px solid ${T.border}`, borderRadius:9, display:"flex", gap:18 }}>
          <span><strong style={{ color:T.text }}>{employees.length}</strong> total</span>
          <span><strong style={{ color:T.red }}>{roots.length}</strong> top-level</span>
          <span><strong style={{ color:T.cyan }}>{employees.length - roots.length}</strong> in hierarchy</span>
        </div>
      )}

      {/* Bottom drop zone */}
      <RootZone active={drag} onDrop={handleDrop}/>

      {/* Toast */}
      {toast && (
        <div style={{
          position:"fixed", bottom:80, left:"50%", transform:"translateX(-50%)",
          padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color: toast.startsWith("✅") ? T.green : T.red,
          boxShadow:T.shadow,
          animation:"slideUp 0.2s ease",
        }}>
          {toast}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes slideUp{from{opacity:0;transform:translateX(-50%) translateY(8px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
    </div>
  );
}